import { useLayoutEffect, useRef } from 'react';

export function useFlipAnimation(isFullscreen, wrapperRef) {
  const hasRenderedNormalRef = useRef(false);

  // Track that the element has rendered at least once in its natural (non-fullscreen)
  // state. Set in the render body (not an effect) so it's always current when effects
  // from the same commit fire.
  if (!isFullscreen) {
    hasRenderedNormalRef.current = true;
  }

  useLayoutEffect(() => {
    if (!wrapperRef.current) return;

    const el = wrapperRef.current;

    const clearFullscreenStyles = () => {
      [
        'position', 'top', 'left', 'width', 'height', 'bottom',
        'margin', 'maxWidth', 'transform', 'transition', 'borderRadius', 'boxShadow',
      ].forEach((p) => { el.style[p] = ''; });
    };

    if (isFullscreen) {
      // Guard: skip if the component never rendered in normal state
      // (e.g. mounted directly with isFullscreen=true — no valid "First" position).
      if (!hasRenderedNormalRef.current) return;

      // Temporarily remove wrapper--fullscreen to measure the card's natural position
      // (the FLIP "First" rect). useLayoutEffect runs before browser paint, so the
      // class toggle is invisible. This always reads current coordinates — unlike a
      // pre-captured rect, it's never stale from a scroll or layout shift.
      const hadFullscreenClass = el.classList.contains('wrapper--fullscreen');
      if (hadFullscreenClass) el.classList.remove('wrapper--fullscreen');
      const rect = el.getBoundingClientRect();
      if (hadFullscreenClass) el.classList.add('wrapper--fullscreen');

      el.style.position = 'fixed';
      el.style.top = `${rect.top}px`;
      el.style.left = `${rect.left}px`;
      el.style.width = `${rect.width}px`;
      el.style.height = `${rect.height}px`;
      el.style.bottom = 'auto';
      el.style.margin = '0';
      el.style.maxWidth = 'none';
      // translateZ(0) keeps the GPU compositing layer active while removing
      // the translateX(-50%) centering applied by .wrapper--desktop.
      // Using transform: none would drop the layer and cause a repaint flash.
      el.style.transform = 'translateZ(0)';
      el.style.transition = 'none';

      let raf1 = null;
      let raf2 = null;

      // Double RAF ensures the browser has committed the pinned styles before
      // the transition is activated — a single RAF is not reliable for this.
      raf1 = requestAnimationFrame(() => {
        raf1 = null;
        raf2 = requestAnimationFrame(() => {
          raf2 = null;
          el.style.transition =
            'top 0.5s ease, left 0.5s ease, width 0.5s ease, height 0.5s ease, border-radius 0.5s ease, box-shadow 0.5s ease';
          el.style.top = '0';
          el.style.left = '0';
          el.style.width = '100%';
          el.style.height = '100dvh';
          el.style.borderRadius = '0';
          el.style.boxShadow = 'none';
        });
      });

      return () => {
        if (raf1) { cancelAnimationFrame(raf1); raf1 = null; }
        if (raf2) { cancelAnimationFrame(raf2); raf2 = null; }
        clearFullscreenStyles();
      };
    }

    clearFullscreenStyles();
  // isFullscreen is the only real dependency; wrapperRef is a stable ref.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFullscreen]);
}
